import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { AuthService } from '../../../core/services/auth.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { SpsPaymentPageComponent } from '../../../custom/components/sps-payment-page/sps-payment-page.component';
import { LayoutService } from '../../../layout/services/layout.service';
import { PaymentPageDataCreate } from '../../dtos/payment-page/payment-page-data-create';
import { PaymentPageData } from '../../interfaces/payment-page/payment-page-data';
import { PaymentPages } from '../../interfaces/payment-page/payment-pages';
import { PaymentPageService } from '../../services/payment-page.service';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'app-payment-page',
  imports: [
    SpsPaymentPageComponent,
    Tabs,
    Tab,
    TabList,
    TabPanels,
    TabPanel
  ],
  templateUrl: './payment-page.component.html'
})
export class PaymentPageComponent extends BaseComponent implements OnInit, AfterViewInit {

  /**
   * Payment-Pages Data
   */
  paymentPages: PaymentPages = null;
  paymentPageData: PaymentPageData = null;

  activePaymentPage: string = 'first';

  constructor(public authService: AuthService,
              public i18n: I18nService,
              private messageService: MessageService,
              public layoutService: LayoutService,
              private helperService: HelperService,
              public paymentPageService: PaymentPageService) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.initPaymentPages();
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    );
    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-home',
          i18nLabelKey: 'MENUDASHBOARD',
          label: this.i18n.i18nLng()['MENUDASHBOARD'],
          routerLink: ['/dashboard'],
          routerLinkActive: false
        },
        {
          i18nLabelKey: 'MENUPAYMENTPAGE',
          label: this.i18n.i18nLng()['MENUPAYMENTPAGE'],
          routerLinkActive: true
        }
      ]);
    }, 50);
  }

  /**
   * Init data for all payment pages
   * PaymentPage Service for PaymentPages is LazyLoading for future use, now data will be loaded eager
   */
  private initPaymentPages(activeTab: string = 'first'): void {
    this.paymentPageService.findPage(this.helperService.eagerLazyLoadEvent('name', 1)).subscribe({
      next: result => {
        if (result.successful) {
          this.paymentPages = {
            count: result.totalElements,
            pages: result.payload
          }
          if (this.paymentPages.count === 1) {
            this.paymentPageData = this.paymentPages.pages[0];
          }
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
          this.initEmptyPaymentPages();
        }
        /**
         * set the active tab
         * first payment page on initialization
         * or the new payment page if payment page was added
         */
        if (this.paymentPages.count > 0) {
          if(activeTab === 'first') {
            this.activePaymentPage = this.paymentPages.pages[0].id;
          } else {
            this.activePaymentPage = activeTab;
          }
        }
        this.loading = false;
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
        this.initEmptyPaymentPages();
        this.loading = false;
      }
    })
  }

  private initEmptyPaymentPages(): void {
    this.paymentPages = {
      count: 0,
      pages: []
    }
  }

  /**
   * Save new payment page
   * @param newPaymentPageDto
   * @protected
   */
  protected onAddNewPaymentPage(newPaymentPageDto: PaymentPageDataCreate) {
    this.paymentPageService.create(SERVICE_ENDPOINTS.CREATE_PAYMENT_PAGE, newPaymentPageDto).subscribe({
      next: result => {
        if (result.successful) {
          this.messageService.add({
            life: 3500,
            severity: 'success',
            summary: this.i18n.i18nLng()['ADDNEWPAYMENTPAGE'],
            detail: this.i18n.i18nLng()['HTTPDATACREATESUCCESS']
          });
          this.initPaymentPages(result.dto.id);
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: result.message,
            detail: result.details
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message
        });
      }
    })
  }

  /**
   * save changed payment page data
   * @param changedPaymentPage
   * @protected
   */
  protected onUpdatedPaymentPage(changedPaymentPage: PaymentPageData) {
    this.paymentPageService.update(SERVICE_ENDPOINTS.UPDATE_PAYMENT_PAGE + changedPaymentPage.id, changedPaymentPage).subscribe({
      next: result => {
        if (result.successful) {
          this.initPaymentPages(result.dto.id);
          this.messageService.add({
            life: 3500,
            severity: 'success',
            summary: this.i18n.i18nLng()['EDITPAYMENTPAGEADMIN'],
            detail: this.i18n.i18nLng()['HTTPDATAUPDATESUCCESS']
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message
        });
      }
    })


  }
}
